/**
 * **비식별 집계** — 몇 번 일어났는지만 센다. 누가 했는지는 저장하지 않는다.
 *
 * ## 왜 우리가 직접 세나
 * 방문자 수는 외부 서비스(Cloudflare 웹 분석·GA4)가 세 준다. 그런데 그건
 * "몇 명 왔나"까지고, **"도서관에 몇 번 들어갔나 · 전자책을 몇 쪽까지 읽나"** 는 모른다.
 * 그건 이 서비스만 아는 것이라 여기서 센다.
 *
 * ## 🔴 무엇을 저장하지 않는가 (불변식 1)
 * 개별 기록을 남기지 않는다. **날짜별 카운터 한 줄씩만** 올린다:
 *
 *   2026-08-20 · zone_enter · library · 37
 *
 * 그래서 "누가 도서관에 갔는지"는 저장 구조상 **알 수가 없다.** IP·기기 식별자·쿠키 없음.
 * 지우고 싶은 개인 데이터가 애초에 생기지 않는다.
 *
 * ## 이름을 여기 한 곳에 두는 이유
 * 웹이 보내고 엣지가 받는다. 목록이 두 곳에 있으면 오타 하나로 **조용히 안 세진다** —
 * 화면도 멀쩡하고 에러도 없어서 몇 달 뒤에야 "그 숫자가 왜 0이지"를 겪는다.
 * 엣지는 여기 없는 이름을 **버린다**(외부 입력 검증 — 불변식 7).
 */

/** 셀 수 있는 것. 새로 세고 싶으면 여기에 추가한다(웹·엣지가 같이 본다). */
export const STAT_EVENTS = {
  /** 첫 화면이 떴다 — 방문 수의 근사치. key: 없음 */
  visit: "visit",
  /**
   * 🗺️ **체험공간에 들어갔다.** key: 어디서 들어왔는지.
   *
   * 🔴 `visit`(첫 화면이 떴다)와 **다르다.** 예전에 이 둘을 한 이름으로 세는 바람에
   * 「방문」이 사실은 체험공간 입장 수였다(2026-08-27 수정). 책만 읽고 간 아이는
   * 방문이고 체험공간 입장은 아니다.
   */
  world_enter: "world_enter",
  /**
   * 🏠 **첫 화면에서 무엇을 눌렀나.** key: 누른 것(`create_hero`·`store`·`community` 등).
   *
   * 첫 화면은 아이가 반드시 지나는 갈림길이다. 어디로도 안 가고 나가는지, 히어로부터
   * 만드는지, 굿즈로 새는지 — 화면을 고칠 때 이 숫자가 근거가 된다.
   */
  entry_click: "entry_click",
  /** 체험공간 존 입장. key: 존 이름(lobby·library·gyeongju·event·goods) */
  zone_enter: "zone_enter",
  /**
   * 📖 **책을 끝까지 봤다.** key: 어느 책인지.
   *
   * 마지막 쪽 다음 화면에서 무언가를 누른 순간 올라간다. `book_page` 는 「몇 쪽까지」를
   * 보여 주지만 **어느 책**인지는 모른다(키가 쪽 번호라서). 완독은 책별로 봐야 뜻이 있다 —
   * 어떤 책이 끝까지 읽히는지가 다음 책을 만드는 근거다.
   */
  book_done: "book_done",
  /** 전자책 쪽 넘김. key: 쪽 번호 */
  book_page: "book_page",
  /**
   * 🛒 **[사러 가기] 를 눌렀다.** key: 상품 id(모르면 어디서 눌렀는지 — `landing`·`world`).
   *
   * 실제 주문은 스마트스토어에서 일어나 우리가 볼 수 없다. **우리가 볼 수 있는 유일한
   * 앞선 신호**가 이것이다 — 굿즈를 계속할지 접을지는 이 숫자로 판단한다
   * (32번 기획안 §7·§8: 판매 개시 8주 뒤 주문 10건).
   *
   * 네 곳에서 누른다(첫 화면·체험공간 가게·굿즈 상세·책 끝). 예전엔 GA4 로만 보내서
   * **한 건도 안 남았다**(2026-08-28에 들여옴).
   */
  goods_click: "goods_click",
  /** 굿즈 상세 열람. key: 상품 id */
  goods_view: "goods_view",
  /** 체험공간 오브젝트(카드·NPC) 열기. key: 오브젝트 id */
  object_open: "object_open",
  /**
   * 📷 **AR 을 열었다.** key: 어느 카드인지.
   *
   * 아래 `ar_fallback` 의 **분모**다. 실패 수만 세면 「많다/적다」를 알 수 없다 —
   * 열어 본 수와 같이 봐야 "카메라가 실제로 되는가"를 판단할 수 있다(FR-AR-07 실인식률).
   */
  ar_open: "ar_open",
  /**
   * 📷 **카메라 대신 영상으로 넘어갔다.** key: 왜(권한 거부·인식 실패 등).
   *
   * AR 은 이 서비스에서 손이 가장 많이 간 기능인데, 아이 폰에서 실제로 되는지
   * **아무도 몰랐다.** 폴백은 조용히 동작해서 화면에는 아무 이상이 없다.
   */
  ar_fallback: "ar_fallback",
  /**
   * 📸 **포토존에서 어디까지 갔나.** key: `open`·`camera`·`capture`·`save`·`share`.
   *
   * 🔴 포토존은 네 자리에서 신호를 쏘고 있었는데 **여기에 이름이 없어서 통째로 버려졌다**
   * (2026-09-02, `pnpm check:statfields` 가 잡음). 운영자는 이 기능이 쓰이는지조차 몰랐다.
   *
   * 넷을 갈라야 쓸모가 있다 — **열었다↔카메라 켜짐** 사이가 벌어지면 권한·기기 문제고,
   * 찍고 안 남기면 저장 단추를 못 찾은 것이다. 한 숫자로는 어느 쪽인지 알 수 없다.
   *
   * 🔴 `open` 을 **카메라가 켜진 뒤에** 보내던 때가 있었다(2026-09-02 수정). 그러면
   * 「열었는데 못 찍은 아이」가 통째로 빠져서, 이 숫자를 보는 유일한 이유가 사라진다.
   */
  ar_photo: "ar_photo",
  /**
   * 🎧 **읽어주기를 들었다.** key: 한국어/영어.
   *
   * 더빙은 운영자가 손으로 만들고 **돈이 든다**(TTS). 아무도 안 들으면 더 만들 이유가 없고,
   * 많이 들으면 없는 쪽을 먼저 채워야 한다. 그 판단에 쓰는 숫자다.
   */
  audio_play: "audio_play",
  /** 히어로 꾸미기 완료. key: 없음 */
  hero_made: "hero_made",
  /**
   * 🔴 **아이 화면이 깨졌다.** key: 어느 화면인지(`/verse` 등 미리 정한 목록 안에서만).
   *
   * 이것만은 「부가 정보」가 아니다 — 이게 없으면 **아이 폰에서 화면이 하얗게 떠도
   * 아무도 모른다.** 운영자는 아이 폰의 콘솔을 볼 수 없다.
   *
   * 예전엔 GA4 로만 보냈는데 GA4 를 안 켰다(2026-08). 그래서 조심스럽게 만든 신고가
   * **운영에서 통째로 버려지고 있었다** — 화면도 멀쩡하고 에러도 없어서 아무 티가 안 났다.
   *
   * 오류 **원문은 안 보낸다**(닉네임·주소가 섞일 수 있다). 종류·자리는 콘솔에만 남는다.
   */
  app_error: "app_error",
  /**
   * 🤖 **AI 자유 대화가 한 번 나갔다.** key: 어느 AI 로 갔는지(`anthropic`·`openai`·`google`) 또는 `fallback`.
   *
   * 🔴 **이것만 엣지가 직접 센다**(웹이 보내는 것이 아니다). 이유가 둘이다:
   *  · **돈이 걸린 숫자다.** 실제로 모델까지 간 호출만 비용이 된다 — 웹이 보내면
   *    「아이가 물어본 횟수」는 알아도 「우리가 얼마를 썼나」는 모른다.
   *  · 웹이 보내는 값은 밖에서 오는 값이라 부풀릴 수 있다. 청구서와 맞춰 볼 숫자는 그러면 안 된다.
   *
   * 2026-09-21 신설. 조치계획서 ■10 이 「AI 자유 대화 실사용」을 증빙으로 내겠다고 적었는데
   * **아무 데도 안 세고 있었다** — 도슨트는 무상태 프록시라 D1 에 한 줄도 안 남겼다.
   * 대화 내용은 여전히 저장하지 않는다. 날짜·공급자별 **횟수만**이다(불변식 1).
   *
   * `fallback` 은 모델이 실패해 승인된 문답으로 되돌아간 횟수다 — 공급자별 수와 나란히 보면
   * 「자유 대화가 실제로 되고 있나」를 알 수 있다(`ar_open` ↔ `ar_fallback` 과 같은 짝).
   */
  npc_ask: "npc_ask",
} as const;

export type StatEvent = keyof typeof STAT_EVENTS;

/** 한 번에 보낼 수 있는 최대 항목 수 — 장난으로 부풀리는 것을 막는다. */
export const STAT_MAX_BATCH = 40;
/**
 * key 최대 길이. 긴 문자열을 저장소에 밀어 넣지 못하게.
 *
 * 🔴 **64 여야 하는 이유가 있다.** 제일 긴 열쇠는 오류 신호의 `화면|종류|자리` 다:
 * 화면 `/goods-preview`(14) + 종류 24(`errorKindTag`) + 자리 24(`errorSiteTag`) + 칸막이 2 = **64**.
 * 40 이면 엣지가 **말없이 잘라서** 넣는다 — 잘린 자리 이름 둘이 같은 줄로 합쳐지고,
 * 운영자는 그게 잘린 것인지 원래 그런 것인지 알 길이 없다. 상한을 넘겼는지 검사가 지킨다.
 */
export const STAT_KEY_MAX = 64;

export interface StatHit {
  name: StatEvent;
  /** 무엇에 대한 것인지(존 이름·쪽 번호 등). 없으면 빈 문자열로 센다. */
  key?: string;
}

/** 이름이 목록에 있는가. */
export function isStatEvent(name: unknown): name is StatEvent {
  return typeof name === "string" && Object.hasOwn(STAT_EVENTS, name);
}

/**
 * 들어온 것을 **믿지 않고** 거른다(불변식 7). 모르는 이름·긴 key·과한 개수를 잘라낸다.
 * 통계는 부가 기능이라, 이상한 게 오면 **막지 말고 버린다** — 화면이 죽으면 안 된다.
 */
export function sanitizeHits(input: unknown): StatHit[] {
  if (!Array.isArray(input)) return [];
  const out: StatHit[] = [];
  for (const raw of input.slice(0, STAT_MAX_BATCH)) {
    const h = raw as { name?: unknown; key?: unknown };
    if (!isStatEvent(h?.name)) continue;
    // 키는 눈에 보이는 ASCII 만 — 저장소·화면을 깨는 문자를 막는다
    const key = typeof h.key === "string" ? h.key.replace(/[^\x20-\x7e]/g, "").slice(0, STAT_KEY_MAX) : "";
    out.push({ name: h.name, key });
  }
  return out;
}

/** 저장 단위 — 같은 (날짜·이름·키)는 한 줄에 모인다. */
export interface StatRow {
  day: string; // YYYY-MM-DD (UTC)
  name: StatEvent;
  key: string;
  n: number;
}

/** 오늘 날짜(UTC). 서버·클라이언트가 같은 규칙을 쓰도록 여기 둔다. */
export function statDay(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/**
 * 📖 **어느 책의 몇 쪽인가** — 집계 열쇠.
 *
 * 🔴 예전엔 쪽 번호만 실었다(`"8"`). 그런데 책이 셋이라 **세 책의 8쪽이 한 통에 섞였다** —
 * 운영자가 보는 것은 「8쪽에서 28번」인데, 그게 34쪽짜리의 중간인지 12쪽짜리의 끝인지
 * 알 수가 없다. 「어디서 아이들이 그만 읽나」가 이 숫자를 보는 유일한 이유인데
 * 그걸 못 답한다(2026-09-01 운영 실측: 열쇠가 전부 맨 숫자였다).
 *
 * 책 이름을 모르면 쪽 번호만 — 옛 기록과 같은 모양이라 섞이지 않는다.
 */
export function bookPageStatKey(book: unknown, page: unknown): string {
  const p = String(typeof page === "number" || typeof page === "string" ? page : "");
  const b = typeof book === "string" ? book.trim() : "";
  return b ? `${b}:${p}` : p;
}

/** 사람이 읽을 모양 — 옛 기록(쪽 번호만)도 그대로 읽힌다. */
export function bookPageStatSay(key: string): { book?: string; page: string } {
  const i = key.indexOf(":");
  return i < 0 ? { page: key } : { book: key.slice(0, i), page: key.slice(i + 1) };
}

/**
 * 🎧 **어느 책의 읽어주기인가** — 집계 열쇠.
 *
 * 🔴 예전엔 언어만 실었다(`"ko"`). 그런데 바로 위 `audio_play` 설명이 약속하는 쓰임은
 * 「**없는 쪽을 먼저 채운다**」이다 — 어느 책인지를 모르면 그걸 못 정한다.
 * 어제 고친 `bookPageStatKey` 와 같은 모양이다(설명은 책을 말하는데 열쇠엔 없다).
 *
 * 더빙은 운영자가 한 쪽씩 만들고 **돈이 든다**(TTS). 「폴짝 108 · 나무 3」이면
 * 어디에 돈을 쓸지가 바로 보이지만, 「ko 111」은 아무것도 못 정해 준다.
 *
 * 책 이름을 모르면 언어만 — 옛 기록(`ko`·`en`)과 같은 모양이라 섞이지 않는다.
 */
export function audioStatKey(book: unknown, lang: unknown): string {
  const l = lang === "en" ? "en" : "ko"; // 화이트리스트(불변식 7) — 바깥 값이 열쇠가 되지 않게
  const b = typeof book === "string" ? book.trim() : "";
  return b ? `${b}:${l}` : l;
}

/** 사람이 읽을 모양 — 옛 기록(언어만)도 그대로 읽힌다. */
export function audioStatSay(key: string): { book?: string; lang: string } {
  const i = key.indexOf(":");
  return i < 0 ? { lang: key } : { book: key.slice(0, i), lang: key.slice(i + 1) };
}
